import gql from 'graphql-tag';
import React from 'react';
import { Mutation, Query } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { Button } from '../../../components/Bootstrap';
import { Layout, LayoutContent, Navigation } from '../../../components';
import {
  ServerAssignmentsQuery,
  ServerAssignmentsQueryVariables,
  ServerAssignmentsQuery_servers as Server,
} from './__generated__/ServerAssignmentsQuery';
import { CreateServer, CreateServerVariables } from './__generated__/CreateServer';
import { UpdateServer, UpdateServerVariables } from './__generated__/UpdateServer';
import { DeleteServer, DeleteServerVariables } from './__generated__/DeleteServer';
import { ServerModal } from './ServerModal';
import { Pencil } from './Pencil';
import { TrashCan } from './TrashCan';
import './Assignments.scss';

interface ServerAssignmentsRouterParams {
  venueId: string;
  fulfillmentCenterId: string;
}

type RouterProps = RouteComponentProps<ServerAssignmentsRouterParams>;

interface ServerAssignmentsProps extends RouterProps {
  data?: ServerAssignmentsQuery;
  loading: boolean;
  error?: Error;
  venueId: string;
  fulfillmentCenterId: string;
  createServer(options: { variables: CreateServerVariables });
  updateServer(options: { variables: UpdateServerVariables });
  deleteServer(options: { variables: DeleteServerVariables });
}

interface ServerAssignmentsState {
  serverName: string;
  addingServer: boolean;
  editing: boolean;
  editingServer?: Server;
}

const AssignmentRow = ({ venueId, fulfillmentCenterId, server, isEditing, onClickEdit, onClickDelete }) => {
  const onClickEditWrapped = React.useCallback(
    (e) => {
      e.preventDefault();
      onClickEdit(server);
    },
    [onClickEdit, server]
  );

  const onClickDeleteWrapped = React.useCallback(
    (e) => {
      e.preventDefault();
      onClickDelete(server);
    },
    [onClickDelete, server]
  );

  const url = `/venue/${venueId}/fulfillment-centers/${fulfillmentCenterId}/assignments/${server.id}`;

  return (
    <a href={url} className='delivery-assignments-link'>
      {server.name}
      {isEditing && (
        <div className='delivery-assignments-link-icons'>
          <Pencil onClick={onClickEditWrapped} />
          <TrashCan onClick={onClickDeleteWrapped} />
        </div>
      )}
    </a>
  );
};

const createMutation = gql`
  mutation CreateServer($venueId: ID!, $name: String!) {
    createServer(venueId: $venueId, name: $name) {
      id
      name
    }
  }
`;

const updateMutation = gql`
  mutation UpdateServer($venueId: ID!, $serverId: ID!, $name: String!) {
    updateServer(venueId: $venueId, serverId: $serverId, name: $name) {
      id
      name
    }
  }
`;

const deleteMutation = gql`
  mutation DeleteServer($venueId: ID!, $serverId: ID!) {
    deleteServer(venueId: $venueId, serverId: $serverId)
  }
`;

class ServerAssignmentsUnwrapped extends React.Component<ServerAssignmentsProps, ServerAssignmentsState> {
  constructor(props) {
    super(props);

    this.state = {
      serverName: '',
      addingServer: false,
      editing: false,
    };
  }

  toggle = () => {
    this.setState({ addingServer: !this.state.addingServer });
  };

  onChangeServerName = (e) => {
    this.setState({
      serverName: e.target.value,
    });
  };

  toggleEditing = () => {
    this.setState({ editing: !this.state.editing });
  };

  onSubmit = (e) => {
    e.preventDefault();
    if (this.state.addingServer) {
      const { serverName } = this.state;
      this.setState({ addingServer: false, serverName: '' });
      this.props.createServer({
        variables: {
          venueId: this.props.venueId,
          name: serverName,
        },
      });
    } else {
      const { editingServer } = this.state;
      this.setState({ editingServer: undefined });
      this.props.updateServer({
        variables: {
          venueId: this.props.venueId,
          serverId: editingServer.id,
          name: editingServer.name,
        },
      });
    }
  };

  onClickEdit = (editingServer: Server) => {
    this.setState({ editingServer });
  };

  onClickDelete = (server: Server) => {
    this.props.deleteServer({
      variables: {
        venueId: this.props.venueId,
        serverId: server.id,
      },
    });
  };

  toggleEditingServer = () => {
    this.setState({ editingServer: undefined });
  };

  onChangeEditingServer = (e) => {
    this.setState({
      editingServer: {
        ...this.state.editingServer,
        name: e.target.value,
      },
    });
  };

  renderEditButton = () => {
    if (this.state.editing) {
      return (
        <Button className='btn--rounded-padded' type='button' color='primary' onClick={this.toggleEditing}>
          Finish
        </Button>
      );
    } else {
      return (
        <Button className='btn--rounded-padded' type='button' color='outline-primary' onClick={this.toggleEditing}>
          Edit Staff
        </Button>
      );
    }
  };

  renderContent() {
    if (this.props.loading || !this.props.data) {
      return <p>Loading...</p>;
    } else {
      const { fulfillmentCenter, servers } = this.props.data;
      const { venueId, fulfillmentCenterId } = this.props.match.params;
      const title = !!this.state.editing ? 'Edit assignments' : `${fulfillmentCenter.name} assignments`;

      return (
        <div className='delivery-assignments'>
          <div className='delivery-assignments-header'>
            <h1>{title}</h1>
            {this.renderEditButton()}
          </div>
          {servers.map((server) => (
            <AssignmentRow
              key={server.id}
              server={server}
              isEditing={this.state.editing}
              onClickEdit={this.onClickEdit}
              onClickDelete={this.onClickDelete}
              venueId={venueId}
              fulfillmentCenterId={fulfillmentCenterId}
            />
          ))}
          <Button onClick={this.toggle} className='btn--rounded-padded mt-3' color='primary'>
            Add Server
          </Button>
          <ServerModal
            isOpen={this.state.addingServer}
            toggle={this.toggle}
            serverName={this.state.serverName}
            onChange={this.onChangeServerName}
            onSubmit={this.onSubmit}
            title='Add Server'
          />
          {this.state.editingServer && (
            <ServerModal
              isOpen={true}
              toggle={this.toggleEditingServer}
              serverName={this.state.editingServer.name}
              onChange={this.onChangeEditingServer}
              onSubmit={this.onSubmit}
              title='Edit Server'
            />
          )}
        </div>
      );
    }
  }

  render() {
    return (
      <Layout>
        <Navigation />
        <Layout.TopBar>
          <Layout.TopBar.Left>
            <Layout.TopBar.Title>Fulfillment Stations</Layout.TopBar.Title>
          </Layout.TopBar.Left>
        </Layout.TopBar>
        <LayoutContent error={this.props.error}>
          <div>{this.renderContent()}</div>
        </LayoutContent>
      </Layout>
    );
  }
}

const query = gql`
  query ServerAssignmentsQuery($venueId: ID!, $fulfillmentCenterId: ID!) {
    fulfillmentCenter(venueId: $venueId, fulfillmentCenterId: $fulfillmentCenterId) {
      id
      name
    }
    servers(venueId: $venueId) {
      id
      name
    }
  }
`;

export const ServerAssignments = (props: RouterProps) => {
  const { venueId, fulfillmentCenterId } = props.match.params;

  return (
    <Query<ServerAssignmentsQuery, ServerAssignmentsQueryVariables>
      variables={{ venueId, fulfillmentCenterId }}
      query={query}
    >
      {({ refetch, loading, error, data }) => {
        return (
          <Mutation<DeleteServer, DeleteServerVariables> mutation={deleteMutation} onCompleted={() => refetch()}>
            {(deleteServer, deleteServerData) => (
              <Mutation<UpdateServer, UpdateServerVariables> mutation={updateMutation} onCompleted={() => refetch()}>
                {(updateServer, updateServerData) => (
                  <Mutation<CreateServer, CreateServerVariables>
                    mutation={createMutation}
                    onCompleted={() => refetch()}
                  >
                    {(createServer, createServerData) => (
                      <ServerAssignmentsUnwrapped
                        {...props}
                        loading={
                          loading || createServerData.loading || updateServerData.loading || deleteServerData.loading
                        }
                        error={error}
                        data={data}
                        venueId={venueId}
                        fulfillmentCenterId={fulfillmentCenterId}
                        createServer={createServer}
                        updateServer={updateServer}
                        deleteServer={deleteServer}
                      />
                    )}
                  </Mutation>
                )}
              </Mutation>
            )}
          </Mutation>
        );
      }}
    </Query>
  );
};
